// FILE: src/pages/AdminTicketDetailPage.jsx
// One support request (plan WP-8 / WP-6, MTO 10.1–10.2, 11.2.1): the
// conversation as message bubbles (student messages on the primary tint,
// staff replies on the page colour, both with body text — AA contrast in
// both themes), a reply box, resolve / reopen, and the service-level
// evidence: the reference, when it was opened and due, the first response,
// when it was resolved and whether that was within 48 hours (icon + words),
// the status history and who answered (with their Digital Driving
// Instructor registration). Every action is awaited inside
// try/catch/finally, so a refused request (e.g. 403
// DDI_REGISTRATION_REQUIRED on a course-content reply) shows why in words
// and the buttons never stay disabled (B-11).
// RBAC: replying and resolving need support.reply; a reply on a
// course-content request also support.reply_content (and, on the server, a
// DDI registration). Without them the page says why instead of the form.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useLocation, useParams } from "react-router-dom";
import { Alert, AlertTitle, Box, Button, Link, Stack, TextField, Typography } from "@mui/material";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import LockPersonOutlinedIcon from "@mui/icons-material/LockPersonOutlined";
import { clearCurrentTicket, fetchTicketDetail, updateTicket } from "../features/ticketSlice";
import { DetailList, EmptyState, ErrorState, LoadingState, PageHeader, Section } from "../components/ui";
import { TicketStatusChip } from "../components/admin/AdminBits";
import { SLA_HOURS, formatDateTime, formatHours, personName, timeAgo } from "../components/admin/format";
import {
  ChannelText,
  SlaChip,
  StatusTimeline,
  WithinTargetChip,
  channelLabel,
  hoursText,
  slaInfo,
  sourceLabel
} from "../components/admin/supportSla";
import { errorCode, errorMessage, messageFor } from "../i18n/errors";
import { roleLabel, ticketCategoryLabel, ticketStatusLabel } from "../i18n/labels";
import usePermissions from "../hooks/usePermissions";

/** Why this staff member can't do something here, as icon + words. */
function NotAllowed({ children, testId }) {
  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start" }} data-testid={testId}>
      <LockPersonOutlinedIcon aria-hidden sx={{ color: "text.secondary", mt: 0.25 }} />
      <Typography sx={{ color: "text.secondary" }}>{children}</Typography>
    </Stack>
  );
}

function Bubble({ from, staff, at, children, now }) {
  return (
    <Box
      component="li"
      sx={{
        display: "flex",
        justifyContent: staff ? "flex-end" : "flex-start",
        mb: 2,
        "&:last-of-type": { mb: 0 }
      }}
    >
      <Box
        sx={{
          maxWidth: { xs: "100%", sm: "85%" },
          px: 2,
          py: 1.5,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          color: "text.primary",
          bgcolor: staff ? "background.default" : "zun.primaryTint",
          borderTopLeftRadius: staff ? undefined : 4,
          borderTopRightRadius: staff ? 4 : undefined
        }}
      >
        <Typography component="p" sx={{ fontWeight: 650 }}>
          {from}
        </Typography>
        <Typography component="p" sx={{ color: "text.secondary", mb: 1 }}>
          <time dateTime={at ? new Date(at).toISOString() : undefined}>{formatDateTime(at)}</time> ({timeAgo(at, now)})
        </Typography>
        <Typography component="div" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
          {children}
        </Typography>
      </Box>
    </Box>
  );
}

/** "3 Oct 2026, 2:10 p.m. (after 3.5 h)". */
function atAfter(at, hours) {
  if (!at) return null;
  const h = formatHours(hours);
  return h === "—" ? formatDateTime(at) : `${formatDateTime(at)} (after ${h})`;
}

export default function AdminTicketDetailPage() {
  const dispatch = useDispatch();
  const { ticketId } = useParams();
  const location = useLocation();
  const backTo = typeof location.state?.from === "string" ? location.state.from : "/admin/support";
  const { currentTicket, detailLoading, detailError } = useSelector((state) => state.tickets);
  const myId = useSelector((state) => state.auth?.user?._id);
  const { can } = usePermissions();

  const [reply, setReply] = useState("");
  const [replyError, setReplyError] = useState("");
  const [busy, setBusy] = useState(null); // "reply" | "resolve" | "reopen"
  const [actionError, setActionError] = useState(null);
  const [notice, setNotice] = useState(() => (typeof location.state?.notice === "string" ? location.state.notice : ""));
  const [now, setNow] = useState(() => Date.now());
  const replyRef = useRef(null);
  const alertRef = useRef(null);

  const load = useCallback(() => {
    setNow(Date.now());
    dispatch(fetchTicketDetail(ticketId));
  }, [dispatch, ticketId]);

  useEffect(() => {
    load();
    return () => {
      dispatch(clearCurrentTicket());
    };
  }, [dispatch, load]);

  // The due time moves on while the page is open.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const ticket = currentTicket && String(currentTicket._id) === String(ticketId) ? currentTicket : null;
  const breadcrumbs = [
    { label: "Support requests", to: backTo },
    { label: ticket?.reference ? `Request ${ticket.reference}` : "Request" }
  ];

  if (!ticket) {
    if (detailError) {
      const missing = ["TICKET_NOT_FOUND", "NOT_FOUND"].includes(errorCode(detailError));
      return (
        <Box>
          <PageHeader title="Support request" breadcrumbs={breadcrumbs} />
          {missing ? (
            <EmptyState
              title={messageFor("TICKET_NOT_FOUND")}
              description="It may have been removed. Go back to the list to choose another request."
              action={
                <Button component={RouterLink} to={backTo} variant="contained">
                  Back to support requests
                </Button>
              }
            />
          ) : (
            <ErrorState error={detailError} title="We couldn't load this request" onRetry={load} />
          )}
        </Box>
      );
    }
    return <LoadingState variant="page" label="Loading the support request…" />;
  }

  const { _id, category, description, status, createdAt, updatedAt, userId, messages = [] } = ticket;
  const student = userId && typeof userId === "object" ? userId : null;
  const studentName = personName(student, "Student");
  const sla = slaInfo(ticket, now);
  const resolved = status === "RESOLVED";
  const reference = ticket.reference || String(_id).slice(-6).toUpperCase();
  const answeredBy = Array.isArray(ticket.answeredBy) ? ticket.answeredBy : [];
  const canAnswer = can("support.reply");
  const canReply = canAnswer && (category !== "CONTENT" || can("support.reply_content"));

  const run = async (kind, updates, done) => {
    setBusy(kind);
    setActionError(null);
    setNotice("");
    try {
      await dispatch(updateTicket({ ticketId: _id, updates })).unwrap();
      setNow(Date.now());
      done();
    } catch (err) {
      setActionError(err || "INTERNAL_ERROR");
      setTimeout(() => alertRef.current?.focus(), 0);
    } finally {
      setBusy(null);
    }
  };

  const sendReply = (e) => {
    e.preventDefault();
    const text = reply.trim();
    if (!text) {
      setReplyError(messageFor("MESSAGE_REQUIRED"));
      replyRef.current?.focus();
      return;
    }
    run("reply", { message: text }, () => {
      setReply("");
      setNotice("Reply sent. The student has been emailed.");
    });
  };

  const setStatus = (next) =>
    run(next === "RESOLVED" ? "resolve" : "reopen", { status: next }, () =>
      setNotice(next === "RESOLVED" ? "Marked as resolved. The student has been emailed." : "Request reopened.")
    );

  const conversation = [
    ...(description ? [{ key: "description", sender: "USER", message: description, createdAt }] : []),
    ...(Array.isArray(messages) ? messages : []).map((m, i) => ({ key: m._id || `m-${i}`, ...m }))
  ];
  const loggedByStaff = ticket.source === "ADMIN";
  const created = new Date(createdAt).getTime();
  const dueAt = ticket.slaDueAt || (Number.isFinite(created) ? new Date(created + SLA_HOURS * 3600000) : null);

  return (
    <Box>
      <PageHeader
        eyebrow={`Reference ${reference}`}
        title={`${ticketCategoryLabel(category)} request`}
        breadcrumbs={breadcrumbs}
        description={`From ${studentName}. Opened ${timeAgo(createdAt, now)}${
          ticket.channel && ticket.channel !== "IN_APP" ? ` by ${channelLabel(ticket.channel).toLowerCase()}` : ""
        }.`}
      >
        <Stack direction="row" sx={{ gap: 0.75, flexWrap: "wrap", mt: 2 }}>
          <TicketStatusChip status={status} />
          <SlaChip ticket={ticket} now={now} showWithinTarget />
        </Stack>
      </PageHeader>

      {actionError && (
        <Alert severity="error" role="alert" ref={alertRef} tabIndex={-1} sx={{ mb: 2 }}>
          <AlertTitle>That didn't go through</AlertTitle>
          {errorMessage(actionError)}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" role="status" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}
      {sla.overdue && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This request is past its {SLA_HOURS}-hour answer time by {hoursText(sla.dueIn)}. Reply or resolve it as soon as you can.
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          alignItems: "start",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.7fr) minmax(0, 1fr)" }
        }}
      >
        <Stack spacing={2.5}>
          <Section id="conversation" title="Conversation">
            {loggedByStaff && (
              <Typography sx={{ color: "text.secondary", mb: 2 }}>
                {sourceLabel(ticket.source, ticket.channel)}: the first message is the staff member's summary of what the student
                asked.
              </Typography>
            )}
            {conversation.length === 0 ? (
              <EmptyState compact title="No messages yet" description="Write the first reply below." />
            ) : (
              <Box component="ol" aria-label="Messages, oldest first" sx={{ listStyle: "none", m: 0, p: 0 }}>
                {conversation.map((m) => {
                  const staff = m.sender === "ADMIN";
                  const summary = m.key === "description" && loggedByStaff;
                  return (
                    <Bubble
                      key={m.key}
                      staff={staff}
                      from={staff ? "Support team" : summary ? `${studentName} (as logged by staff)` : studentName}
                      at={m.createdAt}
                      now={now}
                    >
                      {m.message}
                    </Bubble>
                  );
                })}
              </Box>
            )}
          </Section>

          <Section
            id="reply"
            title="Reply"
            description={
              category === "CONTENT"
                ? "Course content questions are answered by staff with a Digital Driving Instructor registration on their account (BDE Policy Manual 3.4.2)."
                : undefined
            }
          >
            {!canReply ? (
              <NotAllowed testId="reply-not-allowed">
                {canAnswer
                  ? "Answering course-content questions needs the “Answer course-content questions” permission, which your role doesn't include. Ask a registered instructor to reply."
                  : "Your role lets you read support requests but not answer them."}
              </NotAllowed>
            ) : (
              <Box component="form" noValidate onSubmit={sendReply}>
                <TextField
                  id="reply-text"
                  label="Your reply to the student"
                  multiline
                  minRows={4}
                  fullWidth
                  value={reply}
                  inputRef={replyRef}
                  onChange={(e) => {
                    setReply(e.target.value);
                    if (replyError) setReplyError("");
                  }}
                  error={!!replyError}
                  helperText={replyError || "The student gets an email with your reply."}
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2, justifyContent: "flex-end" }}>
                  <Button type="submit" variant="contained" startIcon={<SendOutlinedIcon />} disabled={busy !== null}>
                    {busy === "reply" ? "Sending…" : "Send reply"}
                  </Button>
                </Stack>
              </Box>
            )}
          </Section>
        </Stack>

        <Stack spacing={2.5}>
          <Section id="status" title="Status">
            <Typography sx={{ mb: 2 }}>
              {resolved
                ? "This request is resolved. Reopen it if the student still needs help."
                : "Mark the request as resolved when the student has what they need. They'll be emailed."}
            </Typography>
            {!canAnswer ? (
              <NotAllowed testId="status-not-allowed">Your role doesn't include answering requests, so you can't change their status.</NotAllowed>
            ) : resolved ? (
              <Button
                variant="outlined"
                startIcon={<ReplayOutlinedIcon />}
                onClick={() => setStatus("OPEN")}
                disabled={busy !== null}
                fullWidth
              >
                {busy === "reopen" ? "Reopening…" : "Reopen request"}
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<TaskAltOutlinedIcon />}
                onClick={() => setStatus("RESOLVED")}
                disabled={busy !== null}
                fullWidth
              >
                {busy === "resolve" ? "Marking as resolved…" : "Mark as resolved"}
              </Button>
            )}
          </Section>

          <Section
            id="service-level"
            title="Service level"
            description={`Every request is answered within ${SLA_HOURS} hours (MTO 10.2).`}
          >
            <DetailList
              columns={1}
              items={[
                { label: "Opened", value: formatDateTime(createdAt) },
                { label: "Due by", value: formatDateTime(dueAt) },
                {
                  label: "First response",
                  value: atAfter(ticket.firstResponseAt, ticket.firstResponseHours) || "No staff reply yet"
                },
                {
                  label: "Resolved",
                  value: resolved
                    ? atAfter(ticket.resolvedAt, ticket.resolutionHours) || "Yes (date not recorded)"
                    : ticket.resolvedAt
                      ? `Reopened after being resolved on ${formatDateTime(ticket.resolvedAt)}`
                      : "Not yet"
                },
                { label: `Resolved within ${SLA_HOURS} hours?`, value: <WithinTargetChip ticket={ticket} now={now} /> },
                ticket.reopenedCount > 0 && {
                  label: "Reopened",
                  value: ticket.reopenedCount === 1 ? "Once" : `${ticket.reopenedCount} times`
                }
              ]}
            />
            {ticket.resolvedAtEstimated && (
              <Typography sx={{ color: "text.secondary", mt: 2 }}>
                This request is older than the service-level records, so its resolution time is estimated from its last update.
              </Typography>
            )}
          </Section>

          <Section id="student" title="Student">
            <DetailList
              columns={1}
              items={[
                { label: "Name", value: studentName },
                {
                  label: "Email",
                  value: student?.email ? <Link href={`mailto:${student.email}`}>{student.email}</Link> : "—"
                },
                {
                  label: "Phone",
                  value: student?.phone ? (
                    <Link href={`tel:${String(student.phone).replace(/[^\d+]/g, "")}`}>{student.phone}</Link>
                  ) : (
                    "Not given"
                  )
                },
                student?.role && student.role !== "student" && { label: "Role", value: roleLabel(student.role) }
              ]}
            />
          </Section>

          <Section id="details" title="Request details">
            <DetailList
              columns={1}
              items={[
                { label: "Reference", value: reference },
                { label: "Kind of help", value: ticketCategoryLabel(category) },
                { label: "How it came in", value: <ChannelText channel={ticket.channel} /> },
                { label: "Opened by", value: sourceLabel(ticket.source, ticket.channel) },
                { label: "Status", value: ticketStatusLabel(status) },
                { label: "Last updated", value: formatDateTime(updatedAt) }
              ]}
            />
          </Section>

          <Section id="answered-by" title="Who answered">
            {answeredBy.length === 0 ? (
              <Typography sx={{ color: "text.secondary" }}>No staff reply yet.</Typography>
            ) : (
              <Box component="ol" aria-label="Staff replies, oldest first" sx={{ m: 0, pl: 2.5 }}>
                {answeredBy.map((a, i) => {
                  const me = myId && a.adminId && String(a.adminId) === String(myId);
                  return (
                    <Typography component="li" key={`${a.at}-${i}`} sx={{ mb: 1.5 }}>
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        {me ? "You" : "A staff member"}
                      </Box>
                      , {formatDateTime(a.at)}
                      <Box component="span" sx={{ display: "block", color: "text.secondary" }}>
                        {a.ddiRegistration
                          ? `Digital Driving Instructor registration: ${a.ddiRegistration}`
                          : "No Digital Driving Instructor registration recorded"}
                      </Box>
                    </Typography>
                  );
                })}
              </Box>
            )}
          </Section>

          <Section id="history" title="Status history">
            <StatusTimeline ticket={ticket} myId={myId} />
          </Section>
          {detailLoading && <LoadingState variant="inline" label="Checking for new messages…" />}
        </Stack>
      </Box>
    </Box>
  );
}
